import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { PageHeader } from './PageHeader';
import { Callout } from './Callout';
import { type Phase } from '../../lib/workbook/tokens';

interface TaskPageProps {
  phase: Phase;
  title: string;
  pageNumber: number;
  instruction: string;
  children: React.ReactNode;
  whyItMatters?: string;
  coachRail?: {
    timing?: string;
    tips?: string;
    debrief?: string;
  };
  className?: string;
}

export function TaskPage({ 
  phase, 
  title, 
  pageNumber, 
  instruction, 
  children, 
  whyItMatters,
  coachRail,
  className = ''
}: TaskPageProps) {
  return (
    <div className={`min-h-screen bg-[var(--bg-raised)] ${className}`}>
      <PageHeader 
        phase={phase} 
        title={title} 
        pageNumber={pageNumber} 
      />
      
      <div className="mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-3 space-y-6">
            {/* Instruction */}
            <div className="bg-[var(--surface)] p-4">
              <h2 className="text-lg font-semibold text-[var(--text-pri)] mb-2">
                Do this
              </h2>
              <p className="text-[var(--text-sec)]">{instruction}</p>
            </div>
            
            {/* Content Surface */}
            <div className="space-y-4">
              {children}
            </div>
            
            {/* Why it matters */}
            {whyItMatters && (
              <Callout type="why" phase={phase}>
                {whyItMatters}
              </Callout>
            )}
          </div>
          
          {/* Coach Rail */}
          {coachRail && (
            <div className="lg:col-span-1">
              <div className="bg-[var(--surface)] border border-[var(--rule)] p-4 space-y-4">
                <h3 className="text-sm font-semibold text-[var(--text-pri)]">
                  Coach Rail
                </h3>
                
                {coachRail.timing && (
                  <Callout type="timing">
                    {coachRail.timing}
                  </Callout>
                )}
                
                {coachRail.tips && (
                  <Callout type="tip">
                    {coachRail.tips}
                  </Callout>
                )}
                
                {coachRail.debrief && (
                  <div>
                    <h4 className="text-sm font-medium text-[var(--text-sec)] mb-2">
                      Debrief
                    </h4>
                    <p className="text-sm text-[var(--text-sec)]">{coachRail.debrief}</p>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Navigation */}
        <div className="mt-12 flex justify-between items-center border-t pt-6">
          <div className="flex items-center gap-4">
            <Link
              to={pageNumber > 1 ? `/workbook/${phase}/page-${pageNumber - 1}` : `/workbook/${phase}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              {pageNumber > 1 ? 'Previous' : 'Back to Phase'}
            </Link>
          </div>
          <div className="flex items-center gap-4">
            <Link
              to={`/elevation/workbook/${phase}`}
              className="px-4 py-2 text-[var(--text-sec)] hover:text-[var(--text-pri)] transition-colors"
            >
              Phase Overview
            </Link>
            {pageNumber < 8 && (
              <Link
                to={`/elevation/workbook/${phase}/page-${pageNumber + 1}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--slate)] text-white hover:bg-[var(--slate)] transition-colors"
              >
                Next
                <ArrowRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

